import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Loader } from 'ncoded-component-library';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import { ResponsivePie } from '@nivo/pie';
import { useTranslation } from 'react-i18next';
import utils, { formatPercentage, getPercentageWithTwoDecimals } from 'utils';
import { DATA_ROOM_RFI_STATUS } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  COLORS,
  MARGINS,
  THEME,
  WHTITE_COLOR,
  LEGENDS,
  INNER_RADIUS,
  ARC_LABELS_RADIUS_OFFSET,
  PIE_COLORS,
  MOBILE_THEME,
} from './data';

import './RfiPieChart.styles.scss';
import './RfiPieChart.styles.responsive.scss';

type RfiPieChartProps = {
  className?: string;
};

const RfiPieChart: React.FC<RfiPieChartProps> = (props) => {
  const { className } = props;

  const { isLoading, pending, submitted, closed } = useContext(RfiContext);

  const classes = classNames('rfi-pie-chart', className);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const { rfiStats, totalNumberOfRfis } = useMemo(() => {
    const pendingLenght = pending.length;
    const submittedLength = submitted.length;
    const closedLength = closed.length;

    const totalNumberOfRfis = pendingLenght + submittedLength + closedLength;

    return {
      totalNumberOfRfis,
      rfiStats: [
        ...(pendingLenght
          ? [
              {
                id: DATA_ROOM_RFI_STATUS.PENDING,
                label: t(DATA_ROOM_RFI_STATUS.PENDING),
                color: COLORS.PENDING,
                value: getPercentageWithTwoDecimals(
                  pendingLenght,
                  totalNumberOfRfis,
                ),
              },
            ]
          : []),
        ...(submittedLength
          ? [
              {
                id: DATA_ROOM_RFI_STATUS.SUBMITTED,
                label: t(DATA_ROOM_RFI_STATUS.SUBMITTED),
                color: COLORS.SUBMITTED,
                value: getPercentageWithTwoDecimals(
                  submittedLength,
                  totalNumberOfRfis,
                ),
              },
            ]
          : []),
        ...(closedLength
          ? [
              {
                id: DATA_ROOM_RFI_STATUS.CLOSED,
                label: t(DATA_ROOM_RFI_STATUS.CLOSED),
                color: COLORS.CLOSED,
                value: getPercentageWithTwoDecimals(
                  closedLength,
                  totalNumberOfRfis,
                ),
              },
            ]
          : []),
      ],
    };
  }, [closed.length, pending.length, submitted.length, t]);

  return (
    <div className={classes}>
      {isLoading ? (
        <Loader />
      ) : !rfiStats.length ? (
        <ReportsEmptyState />
      ) : (
        <div className="chart-wrapper">
          <ResponsivePie
            data={rfiStats}
            enableArcLinkLabels={false}
            isInteractive={false}
            margin={MARGINS}
            arcLabelsTextColor={WHTITE_COLOR}
            colors={PIE_COLORS}
            legends={LEGENDS}
            innerRadius={INNER_RADIUS}
            arcLabelsRadiusOffset={ARC_LABELS_RADIUS_OFFSET}
            theme={isPhablet ? MOBILE_THEME : THEME}
            valueFormat={formatPercentage}
          />

          <p className="inner-number">
            {t('requested', {
              count: totalNumberOfRfis,
              requests: utils.getDotedNumber(totalNumberOfRfis),
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default RfiPieChart;
